import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Avatar, Chip, CircularProgress, Alert, IconButton, Stack, Button,
  Dialog, DialogContent, Tabs, Tab, ButtonBase, TextField, MenuItem, LinearProgress, Tooltip,
  useMediaQuery,
} from '@mui/material';
import { useTheme, alpha } from '@mui/material/styles';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import WorkRoundedIcon from '@mui/icons-material/WorkRounded';
import PaymentsRoundedIcon from '@mui/icons-material/PaymentsRounded';
import ApartmentRoundedIcon from '@mui/icons-material/ApartmentRounded';
import SupervisorAccountRoundedIcon from '@mui/icons-material/SupervisorAccountRounded';
import FlagRoundedIcon from '@mui/icons-material/FlagRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import TuneRoundedIcon from '@mui/icons-material/TuneRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import LockRoundedIcon from '@mui/icons-material/LockRounded';
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import WorkspacePremiumRoundedIcon from '@mui/icons-material/WorkspacePremiumRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import TranslateRoundedIcon from '@mui/icons-material/TranslateRounded';
import BoltRoundedIcon from '@mui/icons-material/BoltRounded';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import DeleteOutlineRoundedIcon from '@mui/icons-material/DeleteOutlineRounded';
import EventRoundedIcon from '@mui/icons-material/EventRounded';
import AccessTimeRoundedIcon from '@mui/icons-material/AccessTimeRounded';
import TaskAltRoundedIcon from '@mui/icons-material/TaskAltRounded';
import InsightsRoundedIcon from '@mui/icons-material/InsightsRounded';
import MenuBookRoundedIcon from '@mui/icons-material/MenuBookRounded';
import MailOutlineRoundedIcon from '@mui/icons-material/MailOutlineRounded';
import PhoneRoundedIcon from '@mui/icons-material/PhoneRounded';
import apiService from '../../services/apiServices';

/**
 * Kartela e punonjësit që hapet me klik mbi organigram.
 *
 * Organigrami tregon ku rri dikush sot; kjo tregon kush është dhe si arriti aty:
 * karriera, kualifikimet, projektet ku ka punuar, trajnimet, vlerësimet dhe
 * kontratat. Gjithçka vjen nga një thirrje e vetme, `org-chart/worker/:id`, dhe
 * serveri vendos vetë çfarë lejohet të shihet (paga, dosja personale).
 */

const KIND = {
  hire:        { icon: FlagRoundedIcon,              color: '#10B981' },
  position:    { icon: WorkRoundedIcon,              color: '#6366F1' },
  salary:      { icon: PaymentsRoundedIcon,          color: '#F59E0B' },
  department:  { icon: ApartmentRoundedIcon,         color: '#0EA5E9' },
  manager:     { icon: SupervisorAccountRoundedIcon, color: '#8B5CF6' },
  supervisor:  { icon: SupervisorAccountRoundedIcon, color: '#8B5CF6' },
  termination: { icon: LogoutRoundedIcon,            color: '#F43F5E' },
  contract:    { icon: DescriptionRoundedIcon,       color: '#0EA5E9' },
};
const kindOf = (k) => KIND[k] || { icon: TuneRoundedIcon, color: '#64748B' };

const QUAL = {
  education:   { label: 'Arsimi',       one: 'Arsim',       icon: SchoolRoundedIcon,           color: '#6366F1', titleHint: 'p.sh. Master në Shkenca Kompjuterike', levelHint: 'Bachelor / Master / PhD' },
  certificate: { label: 'Certifikatat', one: 'Certifikatë', icon: WorkspacePremiumRoundedIcon, color: '#F59E0B', titleHint: 'p.sh. AWS Solutions Architect',       levelHint: 'Associate / Professional' },
  license:     { label: 'Licencat',     one: 'Licencë',     icon: BadgeRoundedIcon,            color: '#0EA5E9', titleHint: 'p.sh. Patentë shoferi kategoria B',   levelHint: 'Kategoria' },
  language:    { label: 'Gjuhët',       one: 'Gjuhë',       icon: TranslateRoundedIcon,        color: '#14B8A6', titleHint: 'p.sh. Anglisht',                      levelHint: 'A1 … C2 / Amtare' },
  skill:       { label: 'Aftësitë',     one: 'Aftësi',      icon: BoltRoundedIcon,             color: '#8B5CF6', titleHint: 'p.sh. React, Excel i avancuar',        levelHint: 'Fillestar / Mesatar / Ekspert' },
};
const QUAL_ORDER = ['education', 'certificate', 'license', 'language', 'skill'];

function fmtDate(d) {
  if (!d) return '—';
  const x = new Date(d);
  if (Number.isNaN(x.getTime())) return String(d);
  return x.toLocaleDateString('sq-AL', { day: '2-digit', month: '2-digit', year: 'numeric' });
}
const fmtPeriod = (a, b) => `${a ? fmtDate(a) : '—'} – ${b ? fmtDate(b) : 'sot'}`;

function tenure(from, to) {
  if (!from) return '';
  const a = new Date(from); const b = to ? new Date(to) : new Date();
  let months = (b.getFullYear() - a.getFullYear()) * 12 + (b.getMonth() - a.getMonth());
  if (b.getDate() < a.getDate()) months -= 1;
  if (months < 1) return '< 1 muaj';
  const y = Math.floor(months / 12); const m = months % 12;
  return [y ? `${y} vit${y === 1 ? '' : 'e'}` : '', m ? `${m} muaj` : ''].filter(Boolean).join(' ');
}

function fmtValue(kind, v) {
  if (v === null || v === undefined || v === '') return '—';
  if (kind === 'salary' && !Number.isNaN(Number(v))) {
    return `${Number(v).toLocaleString('sq-AL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €`;
  }
  return String(v);
}
const fmtHours = (h) => `${Number(h || 0).toLocaleString('sq-AL', { maximumFractionDigits: 1 })} orë`;

const STATUS_LABEL = {
  pending: 'Në pritje', pending_supervisor: 'Në pritje', pending_hr: 'Në pritje', pending_management: 'Në pritje',
  approved: 'Aprovuar', scheduled: 'Planifikuar', needs_review: 'Kërkon rishikim',
};

const Pill = ({ label, color, bg, outlined }) => (
  <Chip size="small" label={label} variant={outlined ? 'outlined' : 'filled'}
    sx={{ height: 20, fontSize: '.62rem', fontWeight: 700, ...(outlined ? {} : { bgcolor: bg, color }) }} />
);

function Empty({ icon: Icon, text }) {
  return (
    <Stack alignItems="center" spacing={0.75} sx={{ py: 4, color: 'text.disabled' }}>
      <Icon sx={{ fontSize: 30 }} />
      <Typography sx={{ fontSize: '.8rem' }}>{text}</Typography>
    </Stack>
  );
}

function SectionTitle({ children, action }) {
  return (
    <Stack direction="row" alignItems="center" sx={{ mb: 1, mt: 0.5 }}>
      <Typography sx={{ fontSize: '.66rem', fontWeight: 800, color: 'text.secondary', letterSpacing: '.06em', flex: 1 }}>
        {children}
      </Typography>
      {action}
    </Stack>
  );
}

function Timeline({ items, render }) {
  const theme = useTheme();
  return (
    <Box sx={{ position: 'relative', pl: '34px' }}>
      <Box sx={{ position: 'absolute', left: 13, top: 8, bottom: 8, width: 2, bgcolor: 'divider' }} />
      {items.map((it, i) => {
        const k = kindOf(it.kind);
        const Icon = k.icon;
        return (
          <Box key={i} sx={{ position: 'relative', minHeight: 30, pb: i === items.length - 1 ? 0 : 1.75 }}>
            <Box sx={{
              position: 'absolute', left: -34, top: 0, width: 28, height: 28, borderRadius: '50%',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              bgcolor: alpha(k.color, theme.palette.mode === 'dark' ? 0.3 : 0.14),
              border: `2px solid ${theme.palette.background.paper}`,
            }}>
              <Icon sx={{ fontSize: 15, color: k.color }} />
            </Box>
            {render(it)}
          </Box>
        );
      })}
    </Box>
  );
}

// ── Karriera ────────────────────────────────────────────────────────────────
function CareerTab({ items }) {
  if (!items.length) return <Empty icon={InsightsRoundedIcon} text="Asnjë ndryshim në karrierë i regjistruar." />;
  return (
    <Timeline items={items} render={(it) => (
      <Box sx={{ pt: 0.25 }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '.82rem', fontWeight: 800 }}>{it.title}</Typography>
          {it.date && <Typography sx={{ fontSize: '.7rem', color: 'text.secondary', fontWeight: 600 }}>{fmtDate(it.date)}</Typography>}
          {it.pending && <Pill label={STATUS_LABEL[it.status] || it.status || 'Në pritje'} bg="#FEF3C7" color="#92400E" />}
        </Stack>
        {(it.from || it.to) && (
          <Stack direction="row" alignItems="center" spacing={0.6} sx={{ mt: 0.3, flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '.76rem', color: 'text.secondary' }}>{fmtValue(it.kind, it.from)}</Typography>
            <ArrowForwardIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
            <Typography sx={{ fontSize: '.76rem', fontWeight: 700 }}>{fmtValue(it.kind, it.to)}</Typography>
          </Stack>
        )}
        {it.detail && <Typography sx={{ fontSize: '.72rem', color: 'text.secondary', mt: 0.3, fontStyle: 'italic' }}>{it.detail}</Typography>}
      </Box>
    )} />
  );
}

// ── Kualifikimet ────────────────────────────────────────────────────────────
const EMPTY_Q = { kind: 'education', title: '', institution: '', level: '', obtainedOn: '', expiresOn: '', notes: '' };

function QualificationForm({ workerId, onSaved, onCancel }) {
  const [f, setF] = useState(EMPTY_Q);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const meta = QUAL[f.kind];
  const set = (k) => (e) => setF(x => ({ ...x, [k]: e.target.value }));
  const withExpiry = f.kind === 'certificate' || f.kind === 'license';
  const save = async () => {
    if (!f.title.trim()) { setErr('Shkruaj titullin.'); return; }
    setBusy(true); setErr('');
    try {
      const res = await apiService().post(`org-chart/worker/${workerId}/qualifications`, { ...f, expiresOn: withExpiry ? f.expiresOn : '' });
      if (res?.success === false) throw new Error(res.error || 'Ruajtja dështoi.');
      onSaved();
    } catch (e) {
      setErr(e?.message || 'Ruajtja dështoi.');
    } finally { setBusy(false); }
  };
  return (
    <Box sx={{ p: 1.5, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', bgcolor: 'action.hover' }}>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 2fr' }, gap: 1.25 }}>
        <TextField select size="small" label="Lloji" value={f.kind} onChange={set('kind')}>
          {QUAL_ORDER.map(k => <MenuItem key={k} value={k}>{QUAL[k].one}</MenuItem>)}
        </TextField>
        <TextField size="small" label="Titulli" placeholder={meta.titleHint} value={f.title} onChange={set('title')} autoFocus required />
        {f.kind !== 'skill' && f.kind !== 'language' && (
          <TextField size="small" label="Institucioni" value={f.institution} onChange={set('institution')}
            sx={{ gridColumn: { sm: '1 / -1' } }} placeholder="Universiteti, organizata lëshuese…" />
        )}
        <TextField size="small" label="Niveli" placeholder={meta.levelHint} value={f.level} onChange={set('level')} />
        <Box sx={{ display: 'grid', gridTemplateColumns: withExpiry ? '1fr 1fr' : '1fr', gap: 1.25 }}>
          {f.kind !== 'skill' && (
            <TextField size="small" type="date" label={f.kind === 'education' ? 'Diplomuar më' : 'Marrë më'} value={f.obtainedOn}
              onChange={set('obtainedOn')} InputLabelProps={{ shrink: true }} />
          )}
          {withExpiry && (
            <TextField size="small" type="date" label="Skadon më" value={f.expiresOn}
              onChange={set('expiresOn')} InputLabelProps={{ shrink: true }} />
          )}
        </Box>
        <TextField size="small" label="Shënime" value={f.notes} onChange={set('notes')} multiline maxRows={3} sx={{ gridColumn: '1 / -1' }} />
      </Box>
      {err && <Alert severity="error" sx={{ mt: 1.25, py: 0 }}>{err}</Alert>}
      <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.25 }}>
        <Button size="small" onClick={onCancel} sx={{ textTransform: 'none' }}>Anulo</Button>
        <Button size="small" variant="contained" disabled={busy} onClick={save} sx={{ textTransform: 'none', fontWeight: 700 }}>
          {busy ? 'Duke ruajtur…' : 'Ruaj'}
        </Button>
      </Stack>
    </Box>
  );
}

function QualificationsTab({ data, workerId, onChanged }) {
  const theme = useTheme();
  const [adding, setAdding] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const list = data.qualifications || [];
  const byKind = QUAL_ORDER.map(k => [k, list.filter(q => q.kind === k)]).filter(([, arr]) => arr.length);
  const soon = (d) => d && !Number.isNaN(new Date(d)) && (new Date(d) - new Date()) / 86400000 <= 60;

  const [confirmQ, setConfirmQ] = useState(null);
  const remove = (q) => setConfirmQ(q);
  const doRemove = async () => {
    const q = confirmQ; setConfirmQ(null); setBusyId(q.id);
    try {
      await apiService().delete(`org-chart/worker/${workerId}/qualifications`, q.id);
      onChanged();
    } finally { setBusyId(null); }
  };

  return (
    <Box>
      {data.canEditQualifications && !adding && (
        <Button size="small" startIcon={<AddRoundedIcon />} onClick={() => setAdding(true)}
          sx={{ mb: 1.5, textTransform: 'none', fontWeight: 700 }}>Shto kualifikim</Button>
      )}
      {confirmQ && (
        <Alert severity="warning" sx={{ mb: 1.5, alignItems: 'center' }}
          action={(
            <Stack direction="row" spacing={0.5}>
              <Button size="small" color="inherit" onClick={() => setConfirmQ(null)} sx={{ textTransform: 'none' }}>Anulo</Button>
              <Button size="small" color="error" variant="contained" onClick={doRemove} sx={{ textTransform: 'none' }}>Hiq</Button>
            </Stack>
          )}>
          Të hiqet «{confirmQ.title}»?
        </Alert>
      )}
      {adding && <QualificationForm workerId={workerId} onCancel={() => setAdding(false)} onSaved={() => { setAdding(false); onChanged(); }} />}

      {!byKind.length && !data.fromApplication && !adding && (
        <Empty icon={SchoolRoundedIcon} text="Asnjë kualifikim i regjistruar ende." />
      )}

      {byKind.map(([kind, arr]) => {
        const meta = QUAL[kind]; const Icon = meta.icon;
        // Gjuhët dhe aftësitë lexohen më mirë si etiketa; të tjerat si rreshta me detaje.
        const asChips = kind === 'skill' || kind === 'language';
        return (
          <Box key={kind} sx={{ mb: 2 }}>
            <SectionTitle>{meta.label.toUpperCase()} · {arr.length}</SectionTitle>
            {asChips ? (
              <Stack direction="row" sx={{ flexWrap: 'wrap', gap: 0.75 }}>
                {arr.map(q => (
                  <Chip key={q.id} icon={<Icon sx={{ fontSize: '15px !important', color: `${meta.color} !important` }} />}
                    label={<span><b>{q.title}</b>{q.level ? ` · ${q.level}` : ''}</span>}
                    onDelete={data.canEditQualifications ? () => remove(q) : undefined}
                    disabled={busyId === q.id}
                    sx={{ bgcolor: alpha(meta.color, 0.1), fontSize: '.74rem', height: 28 }} />
                ))}
              </Stack>
            ) : (
              <Stack spacing={1}>
                {arr.map(q => (
                  <Stack key={q.id} direction="row" spacing={1.25} alignItems="flex-start"
                    sx={{ p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider', opacity: busyId === q.id ? 0.5 : 1 }}>
                    <Box sx={{ width: 34, height: 34, borderRadius: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: alpha(meta.color, theme.palette.mode === 'dark' ? 0.3 : 0.12) }}>
                      <Icon sx={{ fontSize: 18, color: meta.color }} />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                        <Typography sx={{ fontSize: '.84rem', fontWeight: 800 }}>{q.title}</Typography>
                        {q.level && <Pill label={q.level} outlined />}
                        {q.expired && <Pill label="Ka skaduar" bg="#FEE2E2" color="#B91C1C" />}
                        {!q.expired && soon(q.expiresOn) && <Pill label="Skadon së shpejti" bg="#FEF3C7" color="#92400E" />}
                      </Stack>
                      {q.institution && <Typography sx={{ fontSize: '.74rem', color: 'text.secondary', fontWeight: 600 }}>{q.institution}</Typography>}
                      <Typography sx={{ fontSize: '.7rem', color: 'text.secondary', mt: 0.2 }}>
                        {q.obtainedOn ? `${kind === 'education' ? 'Diplomuar' : 'Marrë'} më ${fmtDate(q.obtainedOn)}` : ''}
                        {q.obtainedOn && q.expiresOn ? ' · ' : ''}
                        {q.expiresOn ? `Skadon më ${fmtDate(q.expiresOn)}` : ''}
                      </Typography>
                      {q.notes && <Typography sx={{ fontSize: '.72rem', mt: 0.3, fontStyle: 'italic', color: 'text.secondary' }}>{q.notes}</Typography>}
                    </Box>
                    {data.canEditQualifications && (
                      <Tooltip title="Hiq">
                        <IconButton size="small" onClick={() => remove(q)} disabled={busyId === q.id}><DeleteOutlineRoundedIcon fontSize="small" /></IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>
        );
      })}

      {data.fromApplication && (
        <Box sx={{ mt: 1 }}>
          <SectionTitle>NGA APLIKIMI I REKRUTIMIT{data.fromApplication.date ? ` · ${fmtDate(data.fromApplication.date)}` : ''}</SectionTitle>
          <Box sx={{ p: 1.5, borderRadius: 2, bgcolor: 'action.hover' }}>
            {[['Arsimi', data.fromApplication.education], ['Përvoja', data.fromApplication.experience], ['Aftësitë', data.fromApplication.skills]]
              .filter(([, v]) => v).map(([k, v]) => (
                <Box key={k} sx={{ mb: 0.75, '&:last-of-type': { mb: 0 } }}>
                  <Typography sx={{ fontSize: '.66rem', fontWeight: 800, color: 'text.secondary' }}>{k.toUpperCase()}</Typography>
                  <Typography sx={{ fontSize: '.78rem', whiteSpace: 'pre-line' }}>{v}</Typography>
                </Box>
              ))}
          </Box>
        </Box>
      )}
    </Box>
  );
}

// ── Projektet ───────────────────────────────────────────────────────────────
function ProjectsTab({ items }) {
  const theme = useTheme();
  if (!items.length) return <Empty icon={WorkRoundedIcon} text="Nuk është pjesë e asnjë projekti." />;
  const active = items.filter(p => p.ongoing && !p.closed);
  const past = items.filter(p => !(p.ongoing && !p.closed));
  const card = (p) => {
    const c = p.color || theme.palette.primary.main;
    const pct = p.tasksTotal ? Math.round((p.tasksDone / p.tasksTotal) * 100) : null;
    return (
      <Box key={p.id} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider', position: 'relative', overflow: 'hidden',
        '&::before': { content: '""', position: 'absolute', left: 0, top: 0, bottom: 0, width: 4, bgcolor: c } }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap', rowGap: 0.5 }}>
          <Typography sx={{ fontSize: '.86rem', fontWeight: 800 }}>{p.name}</Typography>
          {p.code && <Typography sx={{ fontSize: '.68rem', color: 'text.secondary', fontWeight: 700 }}>{p.code}</Typography>}
          <Box sx={{ flex: 1 }} />
          <Pill label={p.role} bg={alpha(c, 0.14)} color={c} />
          {p.status && <Pill label={p.status} outlined />}
        </Stack>
        <Stack direction="row" spacing={2} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5, color: 'text.secondary' }}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <EventRoundedIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: '.72rem', fontWeight: 600 }}>{fmtPeriod(p.start, p.end)}</Typography>
          </Stack>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <AccessTimeRoundedIcon sx={{ fontSize: 14 }} />
            <Typography sx={{ fontSize: '.72rem', fontWeight: 600 }}>
              {fmtHours(p.hours)} të regjistruara{p.hoursPerDay ? ` · ${p.hoursPerDay} orë/ditë` : ''}
            </Typography>
          </Stack>
          {p.tasksTotal > 0 && (
            <Stack direction="row" spacing={0.5} alignItems="center">
              <TaskAltRoundedIcon sx={{ fontSize: 14 }} />
              <Typography sx={{ fontSize: '.72rem', fontWeight: 600 }}>{p.tasksDone}/{p.tasksTotal} detyra</Typography>
            </Stack>
          )}
        </Stack>
        {pct !== null && (
          <LinearProgress variant="determinate" value={pct}
            sx={{ mt: 1, height: 5, borderRadius: 3, bgcolor: alpha(c, 0.14), '& .MuiLinearProgress-bar': { bgcolor: c, borderRadius: 3 } }} />
        )}
      </Box>
    );
  };
  return (
    <Box>
      {active.length > 0 && (<><SectionTitle>AKTIVE · {active.length}</SectionTitle><Stack spacing={1} sx={{ mb: 2 }}>{active.map(card)}</Stack></>)}
      {past.length > 0 && (<><SectionTitle>TË MËPARSHME · {past.length}</SectionTitle><Stack spacing={1}>{past.map(card)}</Stack></>)}
    </Box>
  );
}

// ── Trajnimet & vlerësimet ──────────────────────────────────────────────────
function DevelopmentTab({ trainings, evaluations }) {
  if (!trainings.length && !evaluations.length) return <Empty icon={MenuBookRoundedIcon} text="Asnjë trajnim apo vlerësim i regjistruar." />;
  return (
    <Box>
      {trainings.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <SectionTitle>TRAJNIMET · {trainings.length}</SectionTitle>
          <Stack spacing={1}>
            {trainings.map(t => (
              <Stack key={t.id} direction="row" spacing={1.25} alignItems="flex-start" sx={{ p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ width: 34, height: 34, borderRadius: 1.5, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: alpha('#14B8A6', 0.12) }}>
                  <MenuBookRoundedIcon sx={{ fontSize: 18, color: '#14B8A6' }} />
                </Box>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                    <Typography sx={{ fontSize: '.84rem', fontWeight: 800 }}>{t.name}</Typography>
                    {t.status && <Pill label={t.status} outlined />}
                  </Stack>
                  <Typography sx={{ fontSize: '.72rem', color: 'text.secondary', fontWeight: 600 }}>
                    {[fmtPeriod(t.start, t.end), t.type, t.trainer && `Trajner: ${t.trainer}`, t.location].filter(Boolean).join(' · ')}
                  </Typography>
                </Box>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}
      {evaluations.length > 0 && (
        <Box>
          <SectionTitle>VLERËSIMET E PERFORMANCËS · {evaluations.length}</SectionTitle>
          <Stack spacing={1}>
            {evaluations.map(e => (
              <Box key={e.id} sx={{ p: 1.25, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
                  <Typography sx={{ fontSize: '.84rem', fontWeight: 800 }}>{[e.period, e.year].filter(Boolean).join(' ') || 'Vlerësim'}</Typography>
                  {e.date && <Typography sx={{ fontSize: '.7rem', color: 'text.secondary', fontWeight: 600 }}>{fmtDate(e.date)}</Typography>}
                  {e.status && <Pill label={e.status} outlined />}
                </Stack>
                {e.result && <Typography sx={{ fontSize: '.78rem', mt: 0.3 }}>{e.result}</Typography>}
                {e.evaluatedBy && <Typography sx={{ fontSize: '.68rem', color: 'text.secondary', mt: 0.2 }}>Vlerësues: {e.evaluatedBy}</Typography>}
              </Box>
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// ── Kontratat ───────────────────────────────────────────────────────────────
function ContractsTab({ items }) {
  if (!items.length) return <Empty icon={DescriptionRoundedIcon} text="Asnjë kontratë e regjistruar." />;
  return (
    <Timeline items={items} render={(it) => {
      const ongoing = !it.endDate || new Date(it.endDate) >= new Date();
      return (
        <Box sx={{ pt: 0.25 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexWrap: 'wrap' }}>
            <Typography sx={{ fontSize: '.82rem', fontWeight: 800 }}>{it.title}</Typography>
            {ongoing && <Pill label="Aktive" bg="#DCFCE7" color="#15803D" />}
            {!it.signed && <Pill label="E panënshkruar" bg="#F3F4F6" color="#6B7280" />}
          </Stack>
          <Typography sx={{ fontSize: '.72rem', color: 'text.secondary', fontWeight: 600, mt: 0.2 }}>{fmtPeriod(it.date, it.endDate)}</Typography>
          {(it.detail || it.salary) && (
            <Typography sx={{ fontSize: '.74rem', mt: 0.2 }}>
              {[it.detail, it.salary ? fmtValue('salary', it.salary) : ''].filter(Boolean).join(' · ')}
            </Typography>
          )}
        </Box>
      );
    }} />
  );
}

function Stat({ value, label }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: typeof value === 'string' ? '.9rem' : '1.1rem', fontWeight: 800, lineHeight: 1.2, whiteSpace: 'nowrap' }}>{value}</Typography>
      <Typography sx={{ fontSize: '.6rem', fontWeight: 700, color: 'text.secondary', letterSpacing: '.04em', lineHeight: 1.3, mt: 0.25 }}>{label}</Typography>
    </Box>
  );
}

function InfoCell({ label, children }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '.6rem', fontWeight: 800, color: 'text.secondary', letterSpacing: '.05em' }}>{label}</Typography>
      <Box sx={{ fontSize: '.8rem', fontWeight: 600, mt: 0.2, minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

export default function OrgWorkerDialog({ workerId, color = '#6366F1', onClose, onOpenWorker }) {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState('career');

  const load = useCallback((keep) => {
    if (!workerId) return () => {};
    let alive = true;
    if (!keep) { setData(null); setError(''); }
    apiService().get(`org-chart/worker/${workerId}`)
      .then(res => {
        if (!alive) return;
        if (res?.success) setData(res.data);
        else setError(res?.error || 'Të dhënat nuk u ngarkuan.');
      })
      .catch(() => alive && setError('Të dhënat nuk u ngarkuan.'));
    return () => { alive = false; };
  }, [workerId]);

  useEffect(() => { setTab('career'); return load(false); }, [load]);

  const name = data ? `${data.firstName || ''} ${data.lastName || ''}`.trim() : '';
  const initials = data ? `${(data.firstName || '').charAt(0)}${(data.lastName || '').charAt(0)}`.toUpperCase() : '';
  const s = data?.stats;
  const tabs = data?.canViewDossier ? [
    ['career', 'Karriera', data.career.length],
    ['qualifications', 'Kualifikimet', data.qualifications.length],
    ['projects', 'Projektet', data.projects.length],
    ['development', 'Trajnime & vlerësime', data.trainings.length + data.evaluations.length],
    ['contracts', 'Kontratat', data.history.length],
  ] : [];

  return (
    <Dialog open={Boolean(workerId)} onClose={onClose} maxWidth="md" fullWidth fullScreen={fullScreen}
      PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 3, overflow: 'hidden', height: fullScreen ? '100%' : 'min(760px, 92vh)', display: 'flex', flexDirection: 'column' } }}>
      {/* Koka: fytyra, emri, roli dhe shifrat kryesore. */}
      <Box sx={{ position: 'relative', flexShrink: 0, px: { xs: 2, sm: 3 }, pt: 2.5, pb: 2,
        background: `linear-gradient(135deg, ${alpha(color, theme.palette.mode === 'dark' ? 0.28 : 0.14)} 0%, ${alpha(color, 0.03)} 100%)`,
        borderBottom: '1px solid', borderColor: 'divider' }}>
        <IconButton size="small" onClick={onClose} sx={{ position: 'absolute', top: 12, right: 12 }}>
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
        {!data && !error && <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress size={28} /></Box>}
        {error && <Alert severity="error" sx={{ mr: 4 }}>{error}</Alert>}
        {data && (
          <>
            <Stack direction="row" spacing={2} alignItems="center">
              <Avatar sx={{ width: 68, height: 68, fontSize: '1.35rem', fontWeight: 800, bgcolor: color, color: '#fff',
                boxShadow: `0 4px 14px ${alpha(color, 0.4)}`, border: `3px solid ${theme.palette.background.paper}` }}>{initials}</Avatar>
              <Box sx={{ minWidth: 0, pr: 4 }}>
                <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, lineHeight: 1.2 }}>{name}</Typography>
                <Typography sx={{ fontSize: '.88rem', color: 'text.secondary', fontWeight: 600 }}>{data.position || '—'}</Typography>
                <Stack direction="row" spacing={0.6} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.5 }}>
                  {data.departmentName && <Pill label={data.departmentName} bg={alpha(color, 0.16)} color={color} />}
                  {data.branchName && <Pill label={data.branchName} outlined />}
                  {data.employeeCode && <Pill label={data.employeeCode} outlined />}
                  {!data.active && <Pill label="Joaktiv" bg="#FEE2E2" color="#B91C1C" />}
                </Stack>
              </Box>
            </Stack>
            {s && (
              <Box sx={{ mt: 2, display: 'grid', gridTemplateColumns: { xs: 'repeat(3, minmax(0, 1fr))', sm: 'repeat(6, minmax(0, 1fr))' }, gap: 1.5,
                p: 1.5, borderRadius: 2, bgcolor: theme.palette.mode === 'dark' ? alpha('#fff', 0.04) : alpha('#fff', 0.75) }}>
                <Stat value={tenure(data.hireDate, data.terminationDate) || '—'} label="VJETËRSIA" />
                <Stat value={s.projects} label={s.activeProjects ? `PROJEKTE · ${s.activeProjects} AKTIVE` : 'PROJEKTE'} />
                <Stat value={Number(s.hours).toLocaleString('sq-AL', { maximumFractionDigits: 1 })} label="ORË PUNE" />
                <Stat value={s.tasksDone} label="DETYRA KRYER" />
                <Stat value={s.qualifications} label="KUALIFIKIME" />
                <Stat value={s.trainings} label="TRAJNIME" />
              </Box>
            )}
          </>
        )}
      </Box>

      {data && (
        <DialogContent sx={{ px: { xs: 2, sm: 3 }, pt: 2, pb: 3, flex: 1, overflowY: 'auto' }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', sm: 'repeat(4, 1fr)' }, gap: 1.75, mb: 2 }}>
            <InfoCell label="EPRORI">
              {data.supervisor ? (
                <ButtonBase onClick={() => onOpenWorker?.(data.supervisor.id)}
                  sx={{ display: 'block', textAlign: 'left', borderRadius: 1, color: 'primary.main', fontWeight: 700, fontSize: '.8rem',
                    '&:hover': { textDecoration: 'underline' } }}>
                  {data.supervisor.name}
                  {data.supervisor.position && <Typography component="span" sx={{ display: 'block', fontSize: '.68rem', color: 'text.secondary' }}>{data.supervisor.position}</Typography>}
                </ButtonBase>
              ) : '—'}
            </InfoCell>
            <InfoCell label="VARTËS DIREKT">{data.directReports || 0}</InfoCell>
            <InfoCell label="FILLOI PUNËN">{fmtDate(data.hireDate)}</InfoCell>
            <InfoCell label="KONTAKTI">
              {data.email && (
                <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
                  <MailOutlineRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography noWrap component="a" href={`mailto:${data.email}`}
                    sx={{ fontSize: '.76rem', fontWeight: 600, color: 'primary.main', textDecoration: 'none' }}>{data.email}</Typography>
                </Stack>
              )}
              {data.phone && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  <PhoneRoundedIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                  <Typography sx={{ fontSize: '.76rem', fontWeight: 600 }}>{data.phone}</Typography>
                </Stack>
              )}
              {!data.email && !data.phone && '—'}
            </InfoCell>
          </Box>

          {!data.canViewDossier ? (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.secondary', p: 2, borderRadius: 2, bgcolor: 'action.hover' }}>
              <LockRoundedIcon fontSize="small" />
              <Typography sx={{ fontSize: '.8rem' }}>Karriera, kualifikimet dhe projektet shihen vetëm nga HR, menaxherët dhe vetë punonjësi.</Typography>
            </Stack>
          ) : (
            <>
              <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto"
                sx={{ minHeight: 38, mb: 2, borderBottom: '1px solid', borderColor: 'divider',
                  '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 700, fontSize: '.82rem', px: 1.5 } }}>
                {tabs.map(([key, label, n]) => (
                  <Tab key={key} value={key} label={
                    <Stack direction="row" spacing={0.6} alignItems="center">
                      <span>{label}</span>
                      <Box component="span" sx={{ px: 0.6, borderRadius: 1, fontSize: '.64rem', bgcolor: 'action.selected' }}>{n}</Box>
                    </Stack>
                  } />
                ))}
              </Tabs>
              {tab === 'career' && <CareerTab items={data.career} />}
              {tab === 'qualifications' && <QualificationsTab data={data} workerId={workerId} onChanged={() => load(true)} />}
              {tab === 'projects' && <ProjectsTab items={data.projects} />}
              {tab === 'development' && <DevelopmentTab trainings={data.trainings} evaluations={data.evaluations} />}
              {tab === 'contracts' && <ContractsTab items={data.history} />}
            </>
          )}
        </DialogContent>
      )}
    </Dialog>
  );
}
